import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  getHouseholds,
  createHousehold,
  assignQR,
  reissueQR,
  batchGenerateQR,
  getBarangays,
} from '../../lib/api';
import {
  QrCode,
  Plus,
  Search,
  Home,
  Tag,
  RefreshCw,
  Download,
  Copy,
  CheckCircle,
} from 'lucide-react';

const TAG_OPTIONS = ['Senior', 'PWD', 'Indigent', 'High-risk'];

export default function HouseholdManagement() {
  const { user, isMunicipalAdmin } = useAuth();
  const [households, setHouseholds] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    barangay_id: '',
    address_label: '',
  });
  const [assignData, setAssignData] = useState({
    address_label: '',
    tags: [],
  });
  const [batchCount, setBatchCount] = useState(10);
  const [batchBarangay, setBatchBarangay] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [copiedQR, setCopiedQR] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [householdsRes, barangaysRes] = await Promise.all([
        getHouseholds(),
        getBarangays(),
      ]);
      setHouseholds(householdsRes.data);
      setBarangays(barangaysRes.data);

      if (!isMunicipalAdmin() && user?.barangay_id) {
        setFormData(prev => ({ ...prev, barangay_id: user.barangay_id }));
        setBatchBarangay(user.barangay_id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load households');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHousehold = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await createHousehold({
        ...formData,
        municipality_id: user.municipality_id,
      });
      toast.success('Household created successfully');
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create household');
    } finally {
      setFormLoading(false);
    }
  };

  const handleBatchGenerate = async () => {
    if (!batchBarangay) {
      toast.error('Please select a barangay');
      return;
    }
    setFormLoading(true);
    
    try {
      const result = await batchGenerateQR(batchCount, batchBarangay);
      toast.success(`Generated ${result.data.count} QR codes`);
      setBatchDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate QR codes');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedHousehold) return;
    setFormLoading(true);
    
    try {
      await assignQR(selectedHousehold.id, assignData);
      toast.success('QR code assigned successfully');
      setAssignDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign QR code');
    } finally {
      setFormLoading(false);
    }
  };

  const handleReissue = async (household) => {
    try {
      await reissueQR(household.id);
      toast.success('QR code reissued successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reissue QR code');
    }
  };

  const copyQRLink = (qrCode) => {
    const link = `${window.location.origin}/r/${qrCode}`;
    navigator.clipboard.writeText(link);
    setCopiedQR(qrCode);
    setTimeout(() => setCopiedQR(null), 2000);
    toast.success('QR link copied to clipboard');
  };

  const openAssignDialog = (household) => {
    setSelectedHousehold(household);
    setAssignData({
      address_label: household.address_label || '',
      tags: household.tags || [],
    });
    setAssignDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      barangay_id: !isMunicipalAdmin() ? user?.barangay_id || '' : '',
      address_label: '',
    });
  };

  const toggleTag = (tag) => {
    setAssignData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const filteredHouseholds = households.filter(household => {
    const matchesSearch = household.address_label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      household.qr_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBarangay = filterBarangay === 'all' || household.barangay_id === filterBarangay;
    const matchesStatus = filterStatus === 'all' || household.qr_status === filterStatus;
    return matchesSearch && matchesBarangay && matchesStatus;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 border-green-300';
      case 'UNASSIGNED': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'DISABLED': return 'bg-red-100 text-red-800 border-red-300';
      case 'REISSUED': return 'bg-slate-100 text-slate-800 border-slate-300';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const stats = {
    total: households.length,
    active: households.filter(h => h.qr_status === 'ACTIVE').length,
    unassigned: households.filter(h => h.qr_status === 'UNASSIGNED').length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="household-management">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Household QR Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage household registrations and QR codes
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="batch-generate-btn">
                  <QrCode className="h-4 w-4 mr-2" />
                  Batch Generate
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Batch Generate QR Codes</DialogTitle>
                  <DialogDescription>
                    Generate multiple unassigned QR codes for later assignment
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {isMunicipalAdmin() && (
                    <div className="space-y-2">
                      <Label>Barangay *</Label>
                      <Select value={batchBarangay} onValueChange={setBatchBarangay}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select barangay" />
                        </SelectTrigger>
                        <SelectContent>
                          {barangays.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Number of QR Codes</Label>
                    <Input
                      type="number"
                      value={batchCount}
                      onChange={(e) => setBatchCount(parseInt(e.target.value) || 10)}
                      min="1"
                      max="100"
                    />
                    <p className="text-xs text-muted-foreground">Maximum 100 per batch</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleBatchGenerate} disabled={formLoading} data-testid="confirm-batch-btn">
                    {formLoading ? 'Generating...' : 'Generate'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-household-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Household
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Household</DialogTitle>
                  <DialogDescription>
                    Create a new household with an assigned address
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateHousehold} className="space-y-4">
                  {isMunicipalAdmin() && (
                    <div className="space-y-2">
                      <Label>Barangay *</Label>
                      <Select
                        value={formData.barangay_id}
                        onValueChange={(v) => setFormData({ ...formData, barangay_id: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select barangay" />
                        </SelectTrigger>
                        <SelectContent>
                          {barangays.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Address *</Label>
                    <Input
                      value={formData.address_label}
                      onChange={(e) => setFormData({ ...formData, address_label: e.target.value })}
                      placeholder="e.g., 123 Main St, Purok 1"
                      required
                      data-testid="address-input"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={formLoading} data-testid="submit-household-btn">
                      {formLoading ? 'Creating...' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Home className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">Total Households</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-sm text-muted-foreground">Active QR Codes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.unassigned > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <QrCode className={`h-8 w-8 ${stats.unassigned > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.unassigned}</p>
                  <p className="text-sm text-muted-foreground">Unassigned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by address or QR code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-household-input"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full md:w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                  <SelectItem value="DISABLED">Disabled</SelectItem>
                </SelectContent>
              </Select>
              {isMunicipalAdmin() && (
                <Select value={filterBarangay} onValueChange={setFilterBarangay}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Barangays</SelectItem>
                    {barangays.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              Households
            </CardTitle>
            <CardDescription>
              {filteredHouseholds.length} households found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>QR Code</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Tags</TableHead>
                    {isMunicipalAdmin() && <TableHead>Barangay</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHouseholds.map((household) => (
                    <TableRow key={household.id} data-testid={`household-row-${household.id}`}>
                      <TableCell className="font-mono text-xs">
                        {household.qr_code.slice(0, 12)}...
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {household.address_label || <span className="text-muted-foreground italic">Not assigned</span>}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium border ${getStatusColor(household.qr_status)}`}>
                          {household.qr_status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {household.tags?.map((tag) => (
                            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                          ))}
                          {(!household.tags || household.tags.length === 0) && '-'}
                        </div>
                      </TableCell>
                      {isMunicipalAdmin() && (
                        <TableCell className="text-sm">{getBarangayName(household.barangay_id)}</TableCell>
                      )}
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyQRLink(household.qr_code)}
                            data-testid={`copy-qr-${household.id}`}
                          >
                            {copiedQR === household.qr_code ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                          {household.qr_status === 'UNASSIGNED' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openAssignDialog(household)}
                              data-testid={`assign-qr-${household.id}`}
                            >
                              Assign
                            </Button>
                          )}
                          {household.qr_status === 'ACTIVE' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReissue(household)}
                              data-testid={`reissue-qr-${household.id}`}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Reissue
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredHouseholds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMunicipalAdmin() ? 6 : 5} className="text-center py-8 text-muted-foreground">
                        No households found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Assign Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign QR Code</DialogTitle>
              <DialogDescription>
                Assign this QR code to a household address
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input
                  value={assignData.address_label}
                  onChange={(e) => setAssignData({ ...assignData, address_label: e.target.value })}
                  placeholder="Enter full address"
                  data-testid="assign-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (Optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map((tag) => (
                    <Button
                      key={tag}
                      type="button"
                      size="sm"
                      variant={assignData.tags.includes(tag) ? 'default' : 'outline'}
                      onClick={() => toggleTag(tag)}
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssign} disabled={formLoading || !assignData.address_label} data-testid="confirm-assign-btn">
                {formLoading ? 'Assigning...' : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
